import React from 'react';
import { HistoricalPosition } from '../../services/adminService';
import { getSignalLabel } from '../../services/klineTypes';
import { POSITION_SIDE } from '@/services/enums';

interface PositionItemProps {
  record: HistoricalPosition;
}

const PositionItem: React.FC<PositionItemProps> = ({ record }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    // uTime 是毫秒时间戳字符串，需要转换为数字
    const timestamp = parseInt(dateString);
    if (isNaN(timestamp)) return '-';
    return new Date(timestamp).toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatNumber = (value: string | null | undefined, decimals: number = 8) => {
    if (!value) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(decimals);
  };

  const getDirectionLabel = (direction?: string | null) => {
    switch (direction) {
      case POSITION_SIDE.LONG:
        return '做多';
      case POSITION_SIDE.SHORT:
        return '做空';
      default:
        return '-';
    }
  };

  const getDirectionColor = (direction?: string | null) => {
    switch (direction) {
      case POSITION_SIDE.LONG:
        return 'bg-green-500/20 text-green-300 border-green-500/50';
      case POSITION_SIDE.SHORT:
        return 'bg-red-500/20 text-red-300 border-red-500/50';
      default:
        return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  const getTypeLabel = (type?: string | null) => {
    switch (type) {
      case '2':
        return '完全平仓';
      case '3':
        return '强平';
      default:
        return '-';
    }
  };

  const getPnLColor = (pnl: string | null | undefined) => {
    if (!pnl) return 'text-white/60';
    const num = parseFloat(pnl);
    if (num > 0) return 'text-green-300';
    if (num < 0) return 'text-red-300';
    return 'text-white/60';
  };

  // const realizedPnl = record.realizedPnl ? parseFloat(record.realizedPnl) : 0; // Unused variable
  // const pnl = record.pnl ? parseFloat(record.pnl) : 0; // Unused variable

  return (
    <div className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90">
      {/* 第一行：交易对、方向和类型 */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap overflow-hidden text-ellipsis block">
            {record.instId}
          </span>
          {record.direction && (
            <span
              className={`px-2 py-1 rounded text-xs border whitespace-nowrap flex-shrink-0 ${getDirectionColor(record.direction)}`}
            >
              {getDirectionLabel(record.direction)}
            </span>
          )}
          {record.type && (
            <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 whitespace-nowrap flex-shrink-0">
              {getTypeLabel(record.type)}
            </span>
          )}
        </div>
      </div>

      {/* 第二行：盈亏信息 */}
      <div className="text-xs text-white/60 mb-2 space-y-1">
        {record.realizedPnl && (
          <div>
            已实现盈亏: <span className={getPnLColor(record.realizedPnl)}>{formatNumber(record.realizedPnl, 4)}</span>
          </div>
        )}
        {record.pnl && (
          <div>
            总盈亏: <span className={getPnLColor(record.pnl)}>{formatNumber(record.pnl, 4)}</span>
          </div>
        )}
        {record.openAvgPx && <div>开仓均价: {formatNumber(record.openAvgPx)}</div>}
        {record.closeAvgPx && <div>平仓均价: {formatNumber(record.closeAvgPx)}</div>}
        {record.openMaxPos && <div>开仓数量: {formatNumber(record.openMaxPos)}</div>}
        {record.closeTotalPos && <div>平仓数量: {formatNumber(record.closeTotalPos)}</div>}
        {record.lever && <div>杠杆: {formatNumber(record.lever, 1)}x</div>}
        {record.fee && <div>手续费: {formatNumber(record.fee, 4)}</div>}
        {record.tag && <div>信号类型: {getSignalLabel(record.tag)}</div>}
        {record.fundingFee && parseFloat(record.fundingFee) !== 0 && (
          <div>资金费率: {formatNumber(record.fundingFee, 4)}</div>
        )}
        {record.liqPenalty && parseFloat(record.liqPenalty) !== 0 && (
          <div className="text-red-400">强平惩罚: {formatNumber(record.liqPenalty, 4)}</div>
        )}
        {record.uTime && <div>平仓时间: {formatDate(record.uTime)}</div>}
      </div>
    </div>
  );
};

export default PositionItem;
